import {
  catchError,
  combineLatestWith,
  distinctUntilChanged,
  EMPTY,
  filter,
  from,
  map,
  type Observable,
  switchMap,
  take,
  tap,
} from 'rxjs'
import { type Member } from '@/features/iam/members/Member'
import type { UserData } from '@/features/iam/users/UserData'
import type { FetchHubspotTokenFn } from '@/features/freshbooks'

/**
 * Identifies the current Pro with the HubSpot chat widget and loads it, once
 * per member.
 */
export function hubspotIntegration(
  currentMember$: Observable<Member>,
  fetchHubspotToken: FetchHubspotTokenFn,
  token$: Observable<string>,
  currentUserData$: Observable<UserData>,
): Observable<unknown> {
  const pros$ = currentMember$.pipe(
    distinctUntilChanged(),
    filter((member) => !member.email.toLowerCase().includes('e2e-')),
    filter((member) => !member.workspace.name.toLowerCase().includes('e2e-')),
    combineLatestWith(currentUserData$),
    filter(([, userData]) => userData.userDataType === 'Pro'),
    map(([member]) => member),
    distinctUntilChanged(),
  )

  return token$.pipe(
    take(1),
    switchMap(() => pros$),
    switchMap((member) =>
      from(fetchHubspotToken()).pipe(
        tap((token) => {
          window.hsConversationsSettings = {
            identificationEmail: member.email,
            identificationToken: token,
          }

          // The embed script loads asynchronously and only runs the ready
          // queue once, when its API becomes available.
          if (window.HubSpotConversations) {
            window.HubSpotConversations.widget.load()
          } else {
            window.hsConversationsOnReady ??= []
            window.hsConversationsOnReady.push(() =>
              window.HubSpotConversations?.widget.load(),
            )
          }
        }),
        // The chat widget is optional; a failure must not end the effect.
        catchError(() => EMPTY),
      ),
    ),
  )
}
